import momentTimezone from 'moment-timezone';
import React, {memo, useCallback, useContext, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {ThemeContext} from '../../apiContexts';
import LoadingBar from '../../components/loadingBar';
import ReactTable from '../../components/reactTable';
import {convertSpeedTo, nomenclatureSpeedOf} from '../../helpers/conversions';
import {getColumnWidth, getWrappedRowHeight, WRAP_COLUMN_MAX_WIDTH} from '../../helpers/report';
import ReplayControls from './replay-controls';
import {Header, ListStyle} from './styles';
import SelectDeviceButton from './selectDeviceButton';

const RouteList = ({
  className,
  data,
  deviceColors,
  timezone,
  onSelect,
  speedUnit,
  loading,
  scrollToIndex,
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const intl = useIntl();

  // O resultado do comando pode ser longo: quebra linha na coluna de observação.
  const observationWidth = useMemo(
      () => Math.min(getColumnWidth(data, 'observation', intl.formatMessage({id: 'reportObservation'})), WRAP_COLUMN_MAX_WIDTH),
      [data, intl],
  );

  const columns = useMemo(
      () => [
        {
          Header: intl.formatMessage({id: 'sharedName'}),
          accessor: 'name',
          minWidth: 200,
          width: getColumnWidth(data, 'name', intl.formatMessage({id: 'sharedName'})),
          // Mesma cor com que o mapa desenha o traco deste dispositivo.
          Cell: ({cell: {value}, row}) => (
            <span style={{color: deviceColors?.get(Number(row.original.deviceId))}}>{value}</span>
          ),
        },
        {
          Header: intl.formatMessage({id: 'reportValid'}),
          accessor: 'valid',
          Cell: ({cell: {value}}) => (value ? intl.formatMessage({id: 'sharedYes'}) : intl.formatMessage({id: 'sharedNo'})),
        },
        {
          Header: intl.formatMessage({id: 'reportDateAndTime'}),
          accessor: 'fixTime',
          Cell: ({cell: {value}}) =>
            momentTimezone(value)
                .tz(timezone)
                .format(intl.formatMessage({id: 'dateTimeLongFormat'})),
          minWidth: 200,
        },
        {
          Header: intl.formatMessage({id: 'positionLatitude'}),
          accessor: 'latitude',
          Cell: ({cell: {value}}) => `${value}º`,
        },
        {
          Header: intl.formatMessage({id: 'positionLongitude'}),
          accessor: 'longitude',
          Cell: ({cell: {value}}) => `${value}º`,
        },
        {
          Header: intl.formatMessage({id: 'positionAltitude'}),
          accessor: 'altitude',
        },
        {
          Header: intl.formatMessage({id: 'reportSpeed'}),
          accessor: 'speed',
          Cell: ({cell: {value}}) =>
            `${Number(convertSpeedTo(value, speedUnit)).toFixed(1) ||
            0} ${nomenclatureSpeedOf(speedUnit)}`,
        },
        {
          Header: intl.formatMessage({id: 'sharedAddress'}),
          accessor: 'address',
          Cell: ({cell: {value}}) => value || '-',
          minWidth: 150,
          width: getColumnWidth(data, 'address', intl.formatMessage({id: 'sharedAddress'})),
        },
        {
          Header: intl.formatMessage({id: 'reportObservation'}),
          accessor: 'observation',
          Cell: ({cell: {value}}) => value || '-',
          width: observationWidth,
          wrap: true,
        },
      ],
      [data, speedUnit, timezone, observationWidth, deviceColors],
  );

  const getRowHeight = useCallback((row) => getWrappedRowHeight(row.observation, observationWidth), [observationWidth]);

  const selectItem = (props) => onSelect(props);

  return (
    <ListStyle theme={theme} className="custom-shadow-2">
      <Header>
        <h5>{intl.formatMessage({id: 'routeHistory'})}</h5>
        <ReplayControls />
        <SelectDeviceButton />
      </Header>
      {loading ? (
        <LoadingBar />
      ) : (
        <ReactTable
          data={data}
          columns={columns}
          selectItem={selectItem}
          scrollToIndex={scrollToIndex}
          getRowHeight={getRowHeight}
        />
      )}
    </ListStyle>
  );
};

export default memo(RouteList);
